import { useCallback, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Inline,
  InlineItem,
  Button,
  Input,
  Select,
  Stack,
} from '@kinesis/bungle';
import { addDays, addYears } from 'date-fns/fp';
import { ScopePicker } from '@/components/scope-picker/scope-picker';
import { noop } from 'lodash/fp';

const expirationOptions = [
  { label: 'No expiration', value: 'none' },
  { label: '30 days', value: '30' },
  { label: '60 days', value: '60' },
  { label: '90 days', value: '90' },
  { label: '1 year', value: '365' },
];

const computeExpiresAt = (option: string): string | null => {
  if (option === 'none') return null;
  const days = Number(option);
  if (days === 365) return addYears(1, new Date()).toISOString();
  return addDays(days, new Date()).toISOString();
};

const AccessKeyCreateModal = (props: AccessKeyCreateModalProps) => {
  const {
    onCancel = noop,
    onCreate = noop,
    isCreating = false,
    animation = true,
  } = props;
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['*']);
  const [expiration, setExpiration] = useState('none');

  const handleCreate = useCallback(() => {
    onCreate(name, scopes, computeExpiresAt(expiration));
  }, [onCreate, name, scopes, expiration]);

  return (
    <Modal
      aria-label='Create access key'
      width={600}
      animation={animation}
      onClose={onCancel}
    >
      <ModalHeader>Create access key</ModalHeader>
      <ModalBody>
        <div style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 140px)' }}>
          <Stack space='medium'>
            <Input
              label='Name'
              placeholder='e.g. CI pipeline, local dev'
              value={name}
              onChange={setName}
              magnitude='large'
            />
            <Select
              label='Expiration'
              magnitude='large'
              options={expirationOptions}
              value={expiration}
              onChange={setExpiration}
            />
            <ScopePicker value={scopes} onChange={setScopes} />
          </Stack>
        </div>
      </ModalBody>
      <ModalFooter>
        <Inline>
          <InlineItem sizing='fill-container' />
          <InlineItem>
            <Button variant='secondary' onClick={onCancel}>
              Cancel
            </Button>
          </InlineItem>
          <InlineItem>
            <Button
              onClick={handleCreate}
              disabled={
                isCreating || scopes.length === 0 || name.trim().length === 0
              }
            >
              Create
            </Button>
          </InlineItem>
        </Inline>
      </ModalFooter>
    </Modal>
  );
};

type AccessKeyCreateModalProps = {
  onCancel?: () => void;
  onCreate?: (name: string, scopes: string[], expiresAt: string | null) => void;
  isCreating?: boolean;
  animation?: boolean;
};

export type { AccessKeyCreateModalProps };
export { AccessKeyCreateModal };
